import { useCallback, useEffect, useRef, useState } from "react";
import { api, API_BASE } from "../../api/client";
import type { NotificationItem, UnreadCount } from "../../api/types";

// 通知欄的狀態:未讀數、清單、面板開關。畫面(鈴鐺與面板)只負責顯示。
//
// --------------------------------------------------------------------------
// 原本是純輪詢,第 13.5 輪改成 SSE + 輪詢當退路
// --------------------------------------------------------------------------
// 第十輪這裡寫的是「輪詢,不做 WebSocket」:未讀數每 30 秒問一次,理由是
// 「一間辦公室幾個人,WebSocket 要多一層連線管理、斷線重連、容器重啟後重建 ——
// 對這個規模是過度工程」。**那個結論錯了,而且錯的不是成本那一半。**
//
// 2026-09-16 業主回報:「通知要重新整理才看到最新的 我要的是 不用整理網頁
// 有通知來就會亮通知/顯示」。查下去有兩個原因:
//
//   1. 分頁切到背景時,瀏覽器會把計時器節流甚至凍住,而切回來時**沒有任何東西
//      觸發補撈** —— 程式碼只在計時器到點時才問。使用者唯一能讓它更新的辦法
//      就是重新整理,與回報的症狀完全吻合。
//   2. 就算分頁一直在前景,最快也要等 30 秒;業主要的「有通知來就會亮」
//      是推播的語意,不是輪詢調快就能換到的東西。
//
// 所以改成 `EventSource`(SSE)接後端的 `GET /api/notifications/stream`。
// **選 SSE 不選 WebSocket**:通知是單向的(伺服器 → 瀏覽器),SSE 正是為這個
// 設計的 —— 走一般 HTTP、自動重連內建(不必自己寫斷線邏輯)、後端只有一個
// uvicorn worker 所以行程內的訂閱簿就夠,不必架 Redis。當年那句「多一層連線
// 管理」對 WebSocket 仍然成立,只是 SSE 根本不收那筆錢。
//
// **輪詢沒有拿掉,只是拉長到 60 秒。** SSE 連不上(公司網路、代理、瀏覽器不支援)
// 時還有東西頂著;做了推播就把退路拆掉,等於把所有雞蛋放進一個會被中間設備
// 砍掉的籃子。另外補上「切回分頁就立刻補撈」—— 那條就算 SSE 一切正常也該有,
// 斷線重連的空窗期靠它補回來,而它正好是上面第 1 點的直接解法。
//
// **輪詢的只有未讀數,不是整份清單。** unread-count 回的是一個數字,
// 清單則是幾十列帶 actor 的資料;面板沒打開還一直撈清單,等於白撈。
//
// 但「只在面板打開的那一下撈一次」也不行:面板開著看一陣子是常有的事 ——
// 鈴鐺從 0 跳到 3,面板底下卻還是剛打開時那幾則,同一個畫面上兩個數字
// 互相打臉,而且要看到內容得先把面板關掉再打開一次,
// 畫面上沒有任何東西提示要這樣做。
//
// 所以**用那個便宜的數字當訊號**:數字與上一次不同、而且面板正開著,才補撈一次
// 清單。數字沒變就一趟都不多花,關著的面板也不撈 —— 沒有人在看。
//
// --------------------------------------------------------------------------
// 讀不到通知,不可以讓整頁跟著壞
// --------------------------------------------------------------------------
// 每一趟都自己吞掉例外。通知是附帶的東西:後端這一支掛了、session 正在換、
// 網路斷一下,使用者該看到的仍然是報價單,不是一片白畫面。
// 唯一的例外是 401 —— 那由 api/client.ts 統一處理(導回登入頁),
// 這裡不另外攔截,否則 session 過期會變成「畫面看起來還在,按什麼都沒反應」。

/**
 * 未讀數的輪詢間隔。
 *
 * 第 13.5 輪從 30 秒拉長到 60 秒:即時亮起那件事已經交給 SSE,這條只剩下
 * 「串流連不上時還有東西頂著」這一個用途,不必再問得那麼密。
 * **但它不可以被拿掉** —— 中間設備砍掉長連線是家常便飯,那時它是唯一的退路。
 */
export const NOTIFICATION_POLL_MS = 60_000;

/**
 * 即時推播的串流。走相對路徑,與其他呼叫同一套規則(見 `api/client.ts`)。
 *
 * 事件裡**沒有任何通知內容**,只有「你有新的」這句話 —— 內容一律由下面那幾支
 * API 重撈。理由見 `onSignal`。
 */
const STREAM_URL = `${API_BASE}/notifications/stream`;

/**
 * 自己重連時的退避階梯(毫秒),最後一格是上限。
 *
 * 用在**瀏覽器已經放棄**的那種斷線上(見下面串流那個 effect)。愈等愈久是
 * 因為會讓這條路斷掉的原因,幾乎都是一時的、而且是全體一起遇到的:部署時
 * api 容器正在重建、上游一次抖動、後端回 429。固定間隔的重試等於每個分頁
 * 都在系統最虛弱的那幾十秒裡每 5 秒敲一次門。
 *
 * 第一格刻意短(5 秒):部署的停機視窗大約二十幾秒,使用者不該為了一次部署
 * 等上一分鐘才恢復即時通知。
 */
export const STREAM_RETRY_MS = [5_000, 15_000, 30_000, 60_000];

/**
 * `EventSource.readyState` 的兩個值,照 HTML 規範定死。
 *
 * 不寫 `EventSource.CLOSED`:那要求全域上真的有 `EventSource` 這個建構子,
 * 而這一段的存在前提正是「它有可能不存在」。
 */
const CONNECTING = 0;
const CLOSED = 2;

/** 面板開關記在瀏覽器的鍵。與章節摺疊同一套做法。 */
export const PANEL_OPEN_KEY = "nq.notifications.open";

/** 一次抓幾則。通知欄是「最近發生了什麼」,不是稽核紀錄。 */
const LIST_LIMIT = 50;

/**
 * 未讀數播報出來是哪一句話。
 *
 * **必須是 `unreadCount` 的純函式。** 摻進時間戳、「剛剛更新」這類會跟著跑的
 * 字,60 秒的輪詢問到同一個數字時那個文字節點也會變 —— 螢幕閱讀器就會每
 * 60 秒把同一個數字再唸一次。文字一模一樣時 React 不會去動那個節點,
 * 那正是「不會重播沒變的數字」唯一的保證來源。
 */
export function describeUnread(count: number): string {
  return count > 0 ? `${count} 則未讀通知` : "沒有未讀通知";
}

/**
 * 面板該不該一開始就是開的。
 *
 * **沒設定過就是關的,螢幕多寬都一樣。** 原本 ≥1536px 會預設展開,業主
 * 2026-09-17 把它取消了:「不要壓縮版面 因為他不太可能會常駐」——
 * 一開站就佔住右邊 22rem,而那一欄大多數時候沒有人在看。
 *
 * 自己開過就記著(那是他當下的選擇,重新整理不該把它忘掉)。
 * localStorage 在隱私模式或封鎖網站資料時讀寫都會丟例外,一律包起來:
 * 記不住只是回到「預設關著」,不該讓整個外框掛掉。
 */
function readOpen(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem(PANEL_OPEN_KEY) === "1";
  } catch {
    /* 讀不到就當作沒設定過 */
    return false;
  }
}

export interface NotificationsState {
  /** 未讀數。鈴鐺上那個數字。 */
  unreadCount: number;
  /**
   * 要播報給螢幕閱讀器的那一句話,掛在鈴鐺旁的即時區域上(見 `AppShell`)。
   *
   * **掛載的第一趟是空字串**,只有在數字真的從一個值變到另一個值之後才有字。
   * 不然每次換頁都會朗讀一次基準值 —— 那不是「有通知來了」,那是雜訊。
   */
  announcement: string;
  /** 面板裡的清單,最新的在最前面(順序由後端決定,前端不重排)。 */
  items: NotificationItem[];
  /** 正在撈清單。 */
  loading: boolean;
  /** 清單這一趟失敗了。**不可以拿它當成「沒有通知」**。 */
  failed: boolean;
  open: boolean;
  toggle: () => void;
  close: () => void;
  /** 標一則為已讀。已經讀過的不重送。 */
  markRead: (item: NotificationItem) => void;
  markAllRead: () => void;
  /**
   * 把**已經讀過**的清掉(業主 2026-09-17:「通知列給我一個 刪除已讀」)。
   *
   * 未讀的一律留著 —— 清掉等於「還沒看就消失了」。
   */
  clearRead: () => void;
  /** 現在有幾則是讀過的。沒有的話那顆鈕按不下去。 */
  readCount: number;
}

export function useNotifications(): NotificationsState {
  const [unreadCount, setUnreadCount] = useState(0);
  const [items, setItems] = useState<NotificationItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [open, setOpen] = useState<boolean>(() => readOpen());
  // 螢幕閱讀器那一句。空字串 = 目前沒有什麼要說的(掛載的第一趟就是這樣)。
  const [announcement, setAnnouncement] = useState("");

  // 卸載之後回來的那一趟不要再 setState。輪詢每 60 秒一次、串流隨時可能推一個
  // 訊號過來,換頁的當下很容易剛好有一趟在路上。
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const rows = await api.get<NotificationItem[]>("/notifications", { limit: LIST_LIMIT });
      if (!alive.current) return;
      setItems(rows);
      setFailed(false);
    } catch {
      // 失敗與空清單是兩件事:面板要寫「讀不到」,不是「目前沒有通知」——
      // 後者是在宣稱一件不知道真假的事。
      if (alive.current) setFailed(true);
    } finally {
      if (alive.current) setLoading(false);
    }
  }, []);

  // 上一次問到的未讀數,與面板現在開著沒有。
  //
  // 兩個都用 ref 而不是相依:``refreshCount`` 一旦因為它們變動就換一個新的函式,
  // 下面那幾個 useEffect 會跟著重掛 —— 計時器每次都從頭開始(面板開開關關幾次,
  // 60 秒的輪詢就永遠等不到下一次),而且**那條串流也會被關掉再開一條**,
  // 後端訂閱簿上同一個人的額度幾下就被自己用光。
  const lastCount = useRef<number | null>(null);
  const openRef = useRef(open);
  openRef.current = open;

  // 「馬上看一眼串流,死掉的話立刻重開」。由串流那個 effect 填,切回分頁時呼叫。
  //
  // 走 ref 而不是把兩個 effect 併成一個:併起來的話,`visibilitychange` 與
  // `focus` 的監聽會跟著串流一起重掛,而它們本來就不該與連線的生死綁在一起。
  const reviveStream = useRef<() => void>(() => {});

  /**
   * 問一次未讀數。
   *
   * `withList` 是給串流用的:面板開著時**不管數字變不變都連清單一起重撈**。
   * 同一類的事件會被後端併成一列(`repeat_count` 加一),那時未讀數一動也不動,
   * 但那一列的內容變了 —— 只看數字的話,面板會停在舊的那一句。
   */
  const refreshCount = useCallback(
    async (options?: { withList?: boolean }) => {
      const listToo = options?.withList === true && openRef.current;
      if (listToo) void loadList();
      try {
        const res = await api.get<UnreadCount>("/notifications/unread-count");
        if (!alive.current) return;
        setUnreadCount(res.count);
        // 第一趟(``lastCount`` 還是 null)不算「變了」:那一趟是拿基準,
        // 而面板若是開著的,打開那一下的 effect 已經撈過清單了。
        const changed = lastCount.current !== null && lastCount.current !== res.count;
        lastCount.current = res.count;
        // ``listToo`` 那一趟已經在路上了,同一個事件不要撈兩份一樣的清單。
        if (changed && openRef.current && !listToo) void loadList();
        // 看得見的人是徽章亮起來,看不見的人就是這一句。**只有真的變了才寫** ——
        // 「第一趟拿基準」與「真的變了」正好是同一個 ``changed`` 分得出來的兩件事。
        if (changed) setAnnouncement(describeUnread(res.count));
      } catch {
        // 讀不到就維持上一次的數字,鈴鐺照常顯示。
      }
    },
    [loadList],
  );

  // 掛上就問一次,之後每 60 秒一次(串流連不上時的退路)。
  useEffect(() => {
    void refreshCount();
    const timer = window.setInterval(() => {
      void refreshCount();
    }, NOTIFICATION_POLL_MS);
    return () => window.clearInterval(timer);
  }, [refreshCount]);

  // --------------------------------------------------------------------------
  // 串流:有通知來就立刻亮
  // --------------------------------------------------------------------------
  //
  // **`error` 一定要接,而且只在 CLOSED 的時候才動手。**(第 13.5 輪審查)
  //
  // 這裡原本寫的是「``error`` 刻意不接:EventSource 自己會重連,這裡插手只會
  // 把它關死」。**那句話只對了一半**,而錯的那一半正好是最常發生的那一半:
  //
  //   對的那一半:**已經建立的連線被斷掉**時(網路抖一下、中間設備砍掉閒置
  //   連線),瀏覽器確實會自己重連,readyState 停在 CONNECTING(0)。
  //   那時插手 new 一條,同一個分頁就有兩條串流 —— 所以那一半照原樣保留。
  //
  //   錯的那一半:**伺服器回了一個非 200 的回應**(或 content-type 不是
  //   text/event-stream)時,HTML 規範要求的是 fail the connection ——
  //   readyState 變 CLOSED(2),**永遠不再重連**,而且沒有人接手的話畫面上
  //   一點跡象都沒有。
  //
  // 而每一次 `deploy.sh` 都必定製造那個回應:api 容器重建、alembic upgrade、
  // 整套 seed 跑完才輪到 uvicorn,那二十幾秒裡 nginx 對 `/api/notifications/stream`
  // 一律回 502(text/html)。EventSource 大約 3 秒重連一次,所以那段時間裡
  // 每一個開著的分頁幾乎必然撞上一次 —— 撞上就永久關閉,從那一刻起只剩 60 秒
  // 輪詢,使用者必須**重新整理**才回得來。那正是業主原話要修掉的症狀。
  // 一次性的 4xx/5xx(公司代理、Cloudflare 的 520/521/522、後端 429)也走同一條路。
  useEffect(() => {
    // jsdom、舊瀏覽器、被政策擋掉的環境裡 ``EventSource`` 根本不存在,
    // 直接 new 下去是 ReferenceError,整個外框會跟著白掉。
    // 沒有就算了 —— 上面那個 60 秒的輪詢本來就是為這種時候留的。
    if (typeof window === "undefined" || typeof window.EventSource === "undefined") return;

    let stream: EventSource | null = null;
    let timer: number | null = null;
    // 卸載之後一條都不准再開。計時器清掉還不夠 —— 已經排進佇列的那一次
    // 回呼還是會跑完,而它開出來的連線沒有人關得掉
    let stopped = false;
    // 連續失敗幾次,決定下一次等多久。連上就歸零
    let attempt = 0;
    // 從上一次連上到現在,中間有沒有斷過。斷過就得補撈(見 ``onOpen``)
    let missed = false;
    // 連 ``new EventSource`` 都丟例外的環境(某些瀏覽器的政策)。
    // 這種不是一時的,重試幾次都一樣,退路交給輪詢
    let refused = false;

    /**
     * 收到訊號就回頭去撈。**事件本身一個欄位都不看。**
     *
     * 串流裡塞內容的話,同一份資料就有兩條路徑會各走各的、遲早漂開;而且
     * 斷線重連時(中間必然有空窗)漏掉的那幾則永遠補不回來 —— 每次都重撈
     * 才是唯一會自己收斂的做法:不論漏了幾則、漏了多久,下一次撈都會拿到
     * 正確的全貌。
     */
    const onSignal = () => {
      void refreshCount({ withList: true });
      // 別的東西也要跟著推播重撈(側邊欄的信箱未讀數,見 mailbox/useMailboxUnread)。
      // 只廣播「有新的」,一樣不帶內容。
      window.dispatchEvent(new Event("nq-live"));
    };

    /** 接上了。退避從頭算;斷過的話,把空窗期漏掉的補回來。 */
    const onOpen = () => {
      attempt = 0;
      if (!missed) return; // 第一次連上:掛載那一趟已經問過了,不必再問一次
      missed = false;
      void refreshCount({ withList: true });
    };

    const onError = () => {
      // 不論哪一種斷法,接回來之前都有一段看不見通知的空窗。
      missed = true;
      if (stream === null) return;
      // CONNECTING(0):瀏覽器自己正在重連 —— 什麼都不做才是對的。
      if (stream.readyState === CONNECTING) return;
      // OPEN(1):一次雜訊,連線還在。
      if (stream.readyState !== CLOSED) return;
      // CLOSED(2):瀏覽器已經放棄了,不會再有下一次 —— 換我們來。
      reconnectLater();
    };

    const forget = () => {
      if (stream === null) return;
      stream.removeEventListener("open", onOpen);
      stream.removeEventListener("message", onSignal);
      stream.removeEventListener("error", onError);
      stream.close();
      stream = null;
    };

    const clearRetry = () => {
      if (timer === null) return;
      window.clearTimeout(timer);
      timer = null;
    };

    const connect = () => {
      if (stopped || refused) return;
      forget();
      let source: EventSource | null = null;
      try {
        source = new window.EventSource(STREAM_URL);
      } catch {
        /* 連 new 都丟例外(某些瀏覽器被政策擋住時會這樣)。退路照舊。 */
        refused = true;
        return;
      }
      stream = source;
      source.addEventListener("open", onOpen);
      source.addEventListener("message", onSignal);
      source.addEventListener("error", onError);
    };

    /** 排一次重連。已經排了就不重排 —— 一次斷線只值一次重連。 */
    const reconnectLater = () => {
      if (stopped || refused || timer !== null) return;
      const wait = STREAM_RETRY_MS[Math.min(attempt, STREAM_RETRY_MS.length - 1)];
      attempt += 1;
      timer = window.setTimeout(() => {
        timer = null;
        connect();
      }, wait);
    };

    // 給下面的「切回分頁」用:使用者都回來了,沒有道理讓他再等一輪退避。
    // 睡醒的筆電、換到手機熱點,都是這一條在救。
    reviveStream.current = () => {
      if (stopped || refused) return;
      // 還開著、或瀏覽器自己正在重連 —— 兩種都不該插手。
      if (stream !== null && stream.readyState !== CLOSED) return;
      clearRetry(); // 排隊中的那一次不必再跑,不然等一下會多開一條
      attempt = 0;
      connect();
    };

    connect();

    return () => {
      stopped = true;
      reviveStream.current = () => {};
      clearRetry();
      // 卸載沒關的話,那條連線會一直留在後端的訂閱簿上(一個人上限 10 條),
      // 換頁幾次就把自己的額度用光。
      forget();
    };
  }, [refreshCount]);

  // --------------------------------------------------------------------------
  // 切回分頁就立刻補撈
  // --------------------------------------------------------------------------
  // **這是業主那個症狀的直接解法。** 瀏覽器會把背景分頁的計時器節流甚至凍住,
  // 切回來時若沒有任何東西觸發補撈,畫面就停在切走的那一刻 —— 使用者唯一的
  // 辦法是重新整理。SSE 斷線重連的空窗期也靠這一條補回來。
  //
  // 兩個事件都接:切分頁只有 `visibilitychange`,切視窗只有 `focus`。
  // 兩個一起來時會多問一次未讀數(一個數字,很便宜),而真正貴的那趟清單
  // 不會跟著重複 —— 第一次就把 ``lastCount`` 更新掉了,第二次看到的是「沒變」。
  useEffect(() => {
    const wake = () => {
      // 看不見的分頁不必補:沒有人在看,而且切回來時還會再補一次。
      if (typeof document !== "undefined" && document.visibilityState === "hidden") return;
      void refreshCount();
      // 順手看一眼串流。睡醒的筆電、換了網路的手機,那條連線常常已經死透了 ——
      // 只補這一次數字的話,接下來又要等到下一次輪詢才會動。
      reviveStream.current();
    };
    document.addEventListener("visibilitychange", wake);
    window.addEventListener("focus", wake);
    return () => {
      document.removeEventListener("visibilitychange", wake);
      window.removeEventListener("focus", wake);
    };
  }, [refreshCount]);

  // 打開的那一下撈一次。之後由未讀數變動來補撈,不定時重撈整份清單。
  useEffect(() => {
    if (!open) return;
    void loadList();
  }, [open, loadList]);

  useEffect(() => {
    try {
      window.localStorage.setItem(PANEL_OPEN_KEY, open ? "1" : "0");
    } catch {
      /* 記不住不影響本次操作 */
    }
  }, [open]);

  const toggle = useCallback(() => setOpen((prev) => !prev), []);
  const close = useCallback(() => setOpen(false), []);

  const markRead = useCallback(
    async (item: NotificationItem) => {
      // 已讀的再點一次不必多送一趟,後端也不會改動 read_at。
      if (item.read_at !== null) return;
      const readAt = new Date().toISOString();
      // 先在畫面上標起來:點下去到伺服器回來之間,那一列不該還寫著「未讀」。
      setItems((prev) => prev.map((row) => (row.id === item.id ? { ...row, read_at: row.read_at ?? readAt } : row)));
      setUnreadCount((prev) => Math.max(0, prev - 1));
      // 自己按掉的那一則也要記進基準,否則下一趟輪詢會把「數字少了一」當成
      // 有新東西,白撈一份自己剛剛才看過的清單。
      if (lastCount.current !== null) lastCount.current = Math.max(0, lastCount.current - 1);
      try {
        await api.post(`/notifications/${item.id}/read`);
      } catch {
        // 沒標成功就把數字校正回來,否則鈴鐺會一直少一。
        void refreshCount();
      }
    },
    [refreshCount],
  );

  const markAllRead = useCallback(async () => {
    const readAt = new Date().toISOString();
    setItems((prev) => prev.map((row) => (row.read_at ? row : { ...row, read_at: readAt })));
    setUnreadCount(0);
    lastCount.current = 0;
    try {
      await api.post("/notifications/read-all");
    } catch {
      void refreshCount();
      void loadList();
    }
  }, [refreshCount, loadList]);

  const clearRead = useCallback(async () => {
    try {
      await api.del("/notifications/read");
    } finally {
      // **清完一定重撈。** 自己在前端把那幾列拿掉的話,重新整理它們又會
      // 全部回來(後端沒刪成功時),而使用者已經以為清乾淨了。
      void loadList();
      void refreshCount();
    }
  }, [loadList, refreshCount]);

  return {
    unreadCount,
    announcement,
    items,
    loading,
    failed,
    open,
    toggle,
    close,
    markRead: (item: NotificationItem) => {
      void markRead(item);
    },
    readCount: items.filter((one) => one.read_at !== null).length,
    clearRead: () => {
      void clearRead();
    },
    markAllRead: () => {
      void markAllRead();
    },
  };
}
